/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import { AuthenticatedTemplate } from '@azure/msal-react';
import { Container } from '@material-ui/core';
import { inject, observer } from 'mobx-react';
import React, { lazy } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import SuspenseWrapper from '../../common/components/SuspenseWrapper';
import { AI_ADMIN_PATH } from '../../common/constants';
import Header from '../../common/header/Header';

const StudioGraphComponent = lazy(() =>
  import(/* webpackChunkName: "stdo" */ './components/studio/StudioGraphComponent')
);
const TestbedConfigure = lazy(() => import(/* webpackChunkName: "tstbd-c" */ './components/testbed/TestbedConfigure'));
const TestbedLauncher = lazy(() => import(/* webpackChunkName: "tstbd-l" */ './components/testbed/TestbedLauncher'));
const QuizAET = lazy(() => import(/* webpackChunkName: "tstbd-q" */ './components/testbed/QuizAET'));
const GuidedStudyTestbedConfigure = lazy(() =>
  import(/* webpackChunkName: "tstbd-c" */ './components/testbed/GuidedStudyTestbedConfigure')
);
const GuidedStudyTestbedLauncher = lazy(() =>
  import(/* webpackChunkName: "tstbd-l" */ './components/testbed/GuidedStudyTestbedLauncher')
);
const OfficeHourTestbedConfigure = lazy(() =>
  import(/* webpackChunkName: "tstbd-c" */ './components/testbed/OfficeHourTestbedConfigure')
);
const OfficeHourTestbedLauncher = lazy(() =>
  import(/* webpackChunkName: "tstbd-l" */ './components/testbed/OfficeHourTestbedLauncher')
);
const OfficeHourAistTestbedConfigure = lazy(() =>
  import(/* webpackChunkName: "tstbd-c" */ './components/testbed/OfficeHourAistTestbedConfigure')
);
const OfficeHourAistTestbedLauncher = lazy(() =>
  import(/* webpackChunkName: "tstbd-l" */ './components/testbed/OfficeHourAistTestbedLauncher')
);

/** Layered radial background for Guided Study testbed routes only. */
const GUIDED_STUDY_TESTBED_MAIN_BACKGROUND_IMAGE = [
  'radial-gradient(60% 60% at 10% 10%, #eadef78c 0 40%, #0000 65%)',
  'radial-gradient(60% 60% at 50% 10%, #fceefb73 0 35%, #0000 65%)',
  'radial-gradient(65% 65% at 90% 10%, #fef9ecd9 0 38%, #0000 65%)',
  'radial-gradient(60% 60% at 10%, #eef2fc80 0 36%, #0000 66%)',
  'radial-gradient(70% 70%, #ffffffa6 0 22%, #0000 60%)',
  'radial-gradient(60% 60% at 90%, #efebf973 0 34%, #0000 66%)',
  'radial-gradient(60% 60% at 10% 90%, #f3f5fc8c 0 40%, #0000 65%)',
  'radial-gradient(60% 60% at 50% 90%, #f4f4fb73 0 40%, #0000 65%)',
  'radial-gradient(60% 60% at 90% 90%, #eff3fb99 0 38%, #0000 66%)'
].join(', ');

const StudioContainer = (props) => {
  const { pathname } = useLocation();
  const isGuidedStudyTestbed =
    pathname === '/berlin/guided-study-testbed' || pathname.startsWith('/berlin/guided-study-testbed/');

  const mainStyle = {
    top: '4.8rem',
    width: '100%',
    margin: '0 auto',
    position: 'relative',
    overflowX: 'auto',
    height: 'auto', // 'calc(100vh - 77px)',
    ...(isGuidedStudyTestbed && {
      backgroundImage: GUIDED_STUDY_TESTBED_MAIN_BACKGROUND_IMAGE,
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover',
      backgroundAttachment: 'fixed'
    })
  };

  return (
    <>
      <Header />
      <main style={mainStyle}>
        <AuthenticatedTemplate>
          <SuspenseWrapper>
            <Switch>
              {/* Classic studio routes — single Route (graph path first) so opening a graph does not remount Studio */}
              <Route
                path={['/berlin/:tenantname/studio/graph/:graphId/:version', '/berlin/:tenantname/studio']}
                exact
                render={() => <StudioGraphComponent {...props} />}
              />

              <Route path="/berlin/:tenantName/quizaet" exact render={() => <QuizAET {...props} />} />

              <Route
                path="/berlin/testbed"
                exact
                render={() => (
                  <Container maxWidth="xl">
                    <TestbedConfigure {...props} />
                  </Container>
                )}
              />
              <Route
                path="/berlin/testbed/:tenantName/launch"
                exact
                render={() => (
                  <Container maxWidth="xl">
                    <TestbedLauncher {...props} />
                  </Container>
                )}
              />

              <Route
                path="/berlin/guided-study-testbed"
                exact
                render={() => (
                  <Container maxWidth="xl">
                    <GuidedStudyTestbedConfigure {...props} />
                  </Container>
                )}
              />
              <Route
                path="/berlin/guided-study-testbed/:tenantName/launch"
                exact
                render={() => (
                  <Container maxWidth="xl">
                    <GuidedStudyTestbedLauncher {...props} />
                  </Container>
                )}
              />

              <Route
                path="/berlin/office-hour-testbed"
                exact
                render={() => <OfficeHourTestbedConfigure {...props} />}
              />

              <Route
                path="/berlin/office-hour-testbed/:tenantName/launch"
                exact
                render={() => <OfficeHourTestbedLauncher {...props} />}
              />

              <Route
                path="/berlin/office-hour-aist-testbed"
                exact
                render={() => <OfficeHourAistTestbedConfigure {...props} />}
              />

              <Route
                path="/berlin/office-hour-aist-testbed/:tenantName/launch"
                exact
                render={() => <OfficeHourAistTestbedLauncher {...props} />}
              />

              <Route render={() => <Redirect to={AI_ADMIN_PATH} />} />
            </Switch>
          </SuspenseWrapper>
        </AuthenticatedTemplate>
      </main>
    </>
  );
};

export default inject()(observer(StudioContainer));
